import { useSelector } from "react-redux";
import { selectSelectedTakeActors } from "../../../app/slices/TrainingSlice";
import { PiInteractionDropdown } from "../../../components/PiInteractionDropdown";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import usePropHelper from "../../../hooks/PropHelper";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { useTranslation } from "react-i18next";
import { isNoLookat, NO_LOOKAT } from "../../../models/NoLookat";

export default function MessageBubbleActorDropdown(props: {
  label: string;
  selectedTargetActorId: string;
  onClick: (actorId: string) => void;
  hideActorsWithProp?: boolean;
  excludeActors?: string[];
  /** Adds "Keep current" (value "") and "No one" (value NO_LOOKAT: stop looking). */
  allowNoActor?: boolean;
  className?: string;
  triggerClassName?: string;
  menuSide?: "top" | "bottom";
}) {
  const {
    label,
    selectedTargetActorId,
    onClick,
    hideActorsWithProp,
    excludeActors,
    allowNoActor,
    className,
    triggerClassName,
    menuSide,
  } = {
    ...props,
  };
  const { t } = useTranslation();
  const actors = useSelector(selectSelectedTakeActors);
  const { getActorsNotAttachedToProps } = usePropHelper();

  const selectedTargetActor = actors.find(
    (actor) => actor.id === selectedTargetActorId
  );

  const filteredActors = hideActorsWithProp
    ? getActorsNotAttachedToProps()
    : actors;

  const noActorDropdownItems = allowNoActor
    ? [
        <PiInteractionDropdown.Item key="keep-current" onClick={() => onClick("")}>
          <div className={"flex justify-center gap-4 w-full"}>
            <RemoveOutlinedIcon fontSize="inherit" color="inherit" />
            <div className={"my-auto truncate"}>{t("chip_select.keep_current")}</div>
          </div>
        </PiInteractionDropdown.Item>,
        <PiInteractionDropdown.Item key="no-actor" onClick={() => onClick(NO_LOOKAT)}>
          <div className={"flex justify-center gap-4 w-full"}>
            <PersonOffOutlinedIcon fontSize="inherit" color="inherit" />
            <div className={"my-auto truncate"}>{t("chip_select.no_actor")}</div>
          </div>
        </PiInteractionDropdown.Item>,
      ]
    : [];

  const emptyLabel = !allowNoActor
    ? "No Actor"
    : isNoLookat(selectedTargetActorId)
      ? t("chip_select.no_actor")
      : t("chip_select.keep_current");

  const actorsDropdownItems = filteredActors
    .filter((actor) => !excludeActors?.some((a) => actor.id === a))
    .map((actor) => {
      return (
        <PiInteractionDropdown.Item
          key={actor.id}
          onClick={() => onClick(actor.id)}
        >
          <div className={"flex justify-center gap-4 w-full"}>
            <ActorAvatar actor={actor} />
            <div className={"my-auto truncate"}>{actor.workingName}</div>
          </div>
        </PiInteractionDropdown.Item>
      );
    });

  const actorDropdown = (
    <PiInteractionDropdown label={label} className={className} triggerClassName={triggerClassName} side={menuSide}>
      <PiInteractionDropdown.Trigger>
        <div className={"flex gap-x-4 w-full"}>
          <ActorAvatar actor={selectedTargetActor} />
          <div className={"my-auto truncate"}>
            {selectedTargetActor?.workingName ?? emptyLabel}
          </div>
        </div>
      </PiInteractionDropdown.Trigger>
      {[...noActorDropdownItems, ...actorsDropdownItems]}
    </PiInteractionDropdown>
  );

  return <>{actorDropdown}</>;
}
